/**
 * `IImporterModel.ts` — the "process / prepare" side of model import.
 *
 * Web port of `Models/ImporterModel/IImporterModel.cs` + `BaseImporterModel.cs`,
 * trimmed to what a browser can do: parse a user-picked GLB or a text-glTF
 * resource bundle with Three.js `GLTFLoader` and walk its hierarchy into
 * Campfire's part / snapshot data model. CAD / FBX / OBJ / point-cloud formats
 * need Pixyz / TriLib (native) and stay server-side — the web consumes the
 * `.glb` those pipelines emit.
 *
 * GLBs are uploaded verbatim. Loose `.gltf` inputs are packaged with their
 * selected `.bin`/texture resources into Unity's `.gltfzip` storage contract.
 */

import type { Emitter } from "@core/events/emitter";
import type { GLTF } from "three/addons/loaders/GLTFLoader.js";

/** `Dictionary<string,float>`-shaped transform component (Unity snapshot space). */
export interface Vec3Map {
  readonly x: number;
  readonly y: number;
  readonly z: number;
}

/**
 * One synthesized part — the inverse of a loaded `PartData` + root
 * `EntitySnapshotData`. `parts[0]` is always the synthetic entity root
 * (`isRoot`, {@link hierarchyId} = `"0,"`); the rest cover `gltf.scene` and its
 * descendants depth-first. `partId` / `snapshotId` are lowercase UUIDs (the
 * Dynamo part/snapshot range reads partition the sk-space by GUID nibble).
 *
 * Transforms are already in **Unity snapshot space** (the renderer applies
 * `snapshotPositionToThreeVec3` / `unityEulerToThreeQuat` to reconstruct the
 * Three.js local), so the project model can persist them without further
 * conversion.
 */
export interface ParsedPart {
  readonly partId: string;
  readonly snapshotId: string;
  readonly name: string;
  readonly displayName: string;
  readonly hierarchyId: string;
  readonly isRoot: boolean;
  readonly position: Vec3Map;
  readonly rotationEuler: Vec3Map;
  readonly scale: Vec3Map;
}

/** Fully parsed model ready for {@link IProjectModel.importModelAsync}. */
export interface ParsedModel {
  /** Raw loader result — injected into the render cache for instant display. */
  readonly gltf: GLTF;
  /** Persisted package bytes: original GLB or canonical Unity `.gltfzip`. */
  readonly blob: Blob;
  readonly fileName: string;
  readonly displayName: string;
  readonly format: "GLB" | "GLTF";
  /** `BestScale(bounds)` — the metres-per-unit factor (= `AssetData.Scale`). */
  readonly assetScale: number;
  /** Persisted `AssetData.ImporterVersion`; drives the renderer's wrap path. */
  readonly importerVersion: string;
  readonly triangleCount: number;
  readonly hasRenderableGeometry: boolean;
  /** Native (pre-scale) bounds center + max dimension for post-import framing. */
  readonly boundsCenter: Vec3Map;
  readonly maxDimension: number;
  readonly parts: ReadonlyArray<ParsedPart>;
}

export interface ImportProgress {
  readonly taskId: string;
  readonly progress: number;
}
export interface ImportCompleted {
  readonly taskId: string;
  readonly fileName: string;
}
export interface ImportFailed {
  readonly taskId: string;
  readonly fileName: string;
  readonly reason: string;
  readonly isCancelled: boolean;
}

export interface IImporterModel {
  readonly onImportProgress: Emitter<ImportProgress>;
  readonly onImportCompleted: Emitter<ImportCompleted>;
  readonly onImportFailed: Emitter<ImportFailed>;

  /** Accepted file extensions (lowercased, with dot) for the picker `accept`. */
  readonly acceptedExtensions: ReadonlyArray<string>;

  /** Whether a filename's extension is importable in-browser. */
  isSupportedFile(fileName: string): boolean;

  /**
   * Parse a picked file and synthesize its {@link ParsedModel}. Emits progress
   * across parse + tree-walk; throws (and emits `onImportFailed`) on an
   * unsupported / corrupt file, incomplete external-resource `.gltf`, or no
   * geometry.
   *
   * `opts.forceMetersScale` skips the unit-guess {@link ParsedModel.assetScale}
   * heuristic and uses scale 1 — for cloud-converted (Pixyz) GLBs, which are
   * already normalized to metres (Unity skips `BestScale` when `ConvertedByPiXYZ`).
   */
  importFromFileAsync(
    file: File,
    signal?: AbortSignal,
    opts?: ImportOptions,
  ): Promise<ParsedModel>;

  /**
   * Parse a picker selection. A GLB/`.gltfzip` uses one file; a loose `.gltf`
   * selection also carries every referenced `.bin` and texture companion.
   */
  importFromFilesAsync(
    files: ReadonlyArray<File>,
    signal?: AbortSignal,
    opts?: ImportOptions,
  ): Promise<ParsedModel>;
}

export interface ImportOptions {
  readonly forceMetersScale?: boolean;
}

/**
 * Companion types shown by the multi-file picker for loose `.gltf` imports.
 * They are resources, not independently importable model roots.
 */
export const GLTF_COMPANION_FILE_EXTENSIONS: ReadonlyArray<string> = [
  ".bin",
  ".dat",
  ".png",
  ".jpg",
  ".jpeg",
  ".webp",
  ".ktx2",
  ".gif",
  ".bmp",
];

export const IMPORTER_MODEL = Symbol.for("Campfire.ImporterModel");
